import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { getSocket } from '../utils/socket';
import Timer from '../components/common/Timer';
import ScoreBoard from '../components/common/ScoreBoard';
import { BASE_URL } from '../utils/api';
import './PlayerGame.css';

export default function PlayerGame() {
  const navigate = useNavigate();
  const {
    groupId,
    groupName,
    groupColor,
    currentQuestion,
    gameStatus,
    gameJustStarted,
    scores,
    scoreDeltas,
    timeLeftMs,
    buzzerState,
    hasAnswered,
    isBlocked,
    waitingReveal,
    revealResult,
    myRevealResult,
    intruVote,
    intruReveal,
    lobbyGroups,
    currentSerieName,
    serieEnded,
    submitText,
    submitChoice,
    submitIntruVote,
    buzz,
  } = useGame();

  const [textAnswer, setTextAnswer] = useState('');
  const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
  const [justification, setJustification] = useState('');
  const [selectedIntru, setSelectedIntru] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [autoSubmitNotice, setAutoSubmitNotice] = useState(false);
  const [hasVotedIntru, setHasVotedIntru] = useState(false);
  const autoSubmittedRef = useRef(false);
  const intruAutoSubmittedRef = useRef(false);
  const prevGameStatusRef = useRef(gameStatus);
  const submitBtnRef = useRef<HTMLButtonElement>(null);
  // Refs to access latest state in socket callbacks
  const selectedIntruRef = useRef(selectedIntru);
  const hasVotedIntruRef = useRef(hasVotedIntru);
  const justificationRef = useRef(justification);
  const intruVoteRef = useRef(intruVote);
  selectedIntruRef.current = selectedIntru;
  hasVotedIntruRef.current = hasVotedIntru;
  justificationRef.current = justification;
  intruVoteRef.current = intruVote;

  useEffect(() => {
    if (!groupName) navigate('/');
  }, [groupName, navigate]);

  // Adjust bottom padding when mobile keyboard appears so submit button stays reachable
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const kbH = Math.max(0, window.innerHeight - vv.height);
      document.documentElement.style.setProperty('--keyboard-h', `${kbH}px`);
      if (kbH > 50) {
        setTimeout(() => submitBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
      }
    };
    vv.addEventListener('resize', update);
    return () => {
      vv.removeEventListener('resize', update);
      document.documentElement.style.removeProperty('--keyboard-h');
    };
  }, []);

  // Only navigate to /scores when status TRANSITIONS to 'ended' (not on initial load)
  useEffect(() => {
    if (prevGameStatusRef.current !== 'ended' && gameStatus === 'ended') {
      navigate('/scores');
    }
    prevGameStatusRef.current = gameStatus;
  }, [gameStatus, navigate]);

  useEffect(() => {
    if (currentQuestion) {
      setTextAnswer('');
      setSelectedChoice(null);
      setJustification('');
      setSelectedIntru(null);
      setFeedback(null);
      setAutoSubmitNotice(false);
      autoSubmittedRef.current = false;
    }
  }, [currentQuestion?.id]);

  useEffect(() => {
    if (intruVote) {
      setSelectedIntru(null);
      setJustification('');
      setHasVotedIntru(false);
      setFeedback(null);
      intruAutoSubmittedRef.current = false;
    }
  }, [intruVote]);

  // Auto-submit intru vote when admin is about to reveal (2s grace window)
  useEffect(() => {
    const socket = getSocket();
    const handleAboutToClose = () => {
      if (intruAutoSubmittedRef.current) return;
      if (hasVotedIntruRef.current) return;
      const idx = selectedIntruRef.current;
      const vote = intruVoteRef.current;
      if (idx === null || !vote) return;
      intruAutoSubmittedRef.current = true;
      const chosenId = vote.musics[idx].id;
      submitIntruVote(chosenId, justificationRef.current.trim()).then((result) => {
        if (result.success) setHasVotedIntru(true);
      });
    };
    socket.on('intru:about_to_close', handleAboutToClose);
    return () => { socket.off('intru:about_to_close', handleAboutToClose); };
  }, [submitIntruVote]);

  // Auto-submit when question ends (timer expired or admin stopped)
  useEffect(() => {
    if (!waitingReveal || hasAnswered || submitting || autoSubmittedRef.current) return;
    if (!currentQuestion) return;

    if (currentQuestion.answerType === 'text' && textAnswer.trim()) {
      autoSubmittedRef.current = true;
      setAutoSubmitNotice(true);
      setSubmitting(true);
      submitText(textAnswer.trim()).then(() => setSubmitting(false));
    } else if (currentQuestion.answerType === 'multiple_choice' && selectedChoice !== null) {
      autoSubmittedRef.current = true;
      setAutoSubmitNotice(true);
      setSubmitting(true);
      submitChoice(selectedChoice).then(() => setSubmitting(false));
    }
  }, [waitingReveal]); // eslint-disable-line

  const handleText = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!textAnswer.trim() || submitting || hasAnswered) return;
    setSubmitting(true);
    const result = await submitText(textAnswer.trim());
    setSubmitting(false);
    if (!result.success) setFeedback(result.error || 'Erreur');
  };

  const handleChoice = async (i: number) => {
    if (hasAnswered || submitting || waitingReveal) return;
    setSubmitting(true);
    const result = await submitChoice(i);
    setSubmitting(false);
    if (!result.success) {
      setSelectedChoice(null);
      setFeedback(result.error || 'Erreur');
    }
  };

  const handleBuzz = async () => {
    if (buzzerState.taken || isBlocked || submitting) return;
    setSubmitting(true);
    const result = await buzz();
    setSubmitting(false);
    if (!result.success) setFeedback(result.error || 'Impossible de buzzer');
  };

  const handleIntruVote = async () => {
    if (selectedIntru === null || hasVotedIntru || submitting) return;
    setSubmitting(true);
    const chosenId = intruVote!.musics[selectedIntru].id;
    const result = await submitIntruVote(chosenId, justification.trim());
    setSubmitting(false);
    if (result.success) {
      setHasVotedIntru(true);
    } else {
      setFeedback(result.error || 'Erreur');
    }
  };

  // ── Intru reveal screen ────────────────────────────────────────────────────
  if (intruReveal) {
    const myResult = intruReveal.results.find((r) => r.groupId === groupId);
    return (
      <div className="player-page">
        <div className="player-card">
          <div className="player-header">
            <span className="player-group" style={{ borderColor: groupColor || undefined }}>{groupName}</span>
          </div>
          <div className={`reveal-result ${myResult?.correct ? 'reveal-correct' : 'reveal-wrong'}`}>
            <div className="reveal-icon">{myResult?.correct ? '✅' : '❌'}</div>
            <div className="reveal-text">
              {myResult?.correct
                ? `Bien trouvé ! +${myResult.points} pts`
                : 'Pas la bonne musique...'}
            </div>
          </div>
          <div className="intru-reveal-answer">
            <strong>L'intru était :</strong>
            <span className="intru-reveal-title"> {intruReveal.intruTitle || intruReveal.intruQuestionId}</span>
            {intruReveal.intruArtist && <span className="intru-reveal-artist"> — {intruReveal.intruArtist}</span>}
            {intruReveal.intruJustification && (
              <blockquote className="intru-justif-player">{intruReveal.intruJustification}</blockquote>
            )}
          </div>
          {scores.length > 0 && <ScoreBoard scores={scores} compact />}
        </div>
      </div>
    );
  }

  // ── Intru vote screen ──────────────────────────────────────────────────────
  if (intruVote) {
    return (
      <div className="player-page">
        <div className="player-card">
          <div className="player-header">
            <span className="player-group" style={{ borderColor: groupColor || undefined }}>{groupName}</span>
          </div>
          <div className="intru-vote-header">
            <div className="intru-vote-icon">🕵</div>
            <h2>Qui est l'intru ?</h2>
            <p className="text-muted">Une de ces musiques ne ressemble pas aux autres...</p>
          </div>

          {hasVotedIntru ? (
            <div className="answered-waiting">
              <div className="answered-check">✓</div>
              <p>Vote envoyé !</p>
              <p className="text-muted">En attente des résultats...</p>
            </div>
          ) : (
            <>
              <div className="intru-vote-list">
                {intruVote.musics.map((music, i) => (
                  <div
                    key={music.id}
                    className={`intru-vote-card ${selectedIntru === i ? 'intru-selected' : ''}`}
                    onClick={() => setSelectedIntru(i)}
                  >
                    <div className="intru-card-indicator">
                      {selectedIntru === i ? '✓' : String.fromCharCode(65 + i)}
                    </div>
                    <div className="intru-card-info">
                      <div className="intru-card-title">{music.songTitle || music.title}</div>
                      {music.artist && <div className="intru-card-artist">{music.artist}</div>}
                      {selectedIntru === i && music.audioFile && (
                        <div className="intru-audio-preview" onClick={(e) => e.stopPropagation()}>
                          <audio
                            src={`${BASE_URL}/api/musics/stream/${music.audioFile}`}
                            controls
                            preload="none"
                          />
                        </div>
                      )}
                    </div>
                    <div className="intru-card-check">✓</div>
                  </div>
                ))}
              </div>

              {feedback && <div className="alert alert-danger">{feedback}</div>}

              <div className="intru-justification">
                <textarea
                  placeholder="Justification (optionnel) — pourquoi est-ce l'intru ?"
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={2}
                />
              </div>

              <button
                className="btn btn-primary btn-full"
                onClick={handleIntruVote}
                disabled={selectedIntru === null || submitting}
              >
                {submitting ? 'Envoi...' : 'Valider mon vote'}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Reveal result screen ───────────────────────────────────────────────────
  if (myRevealResult !== null || (revealResult && groupId)) {
    const correct = myRevealResult?.correct;
    const pts = myRevealResult?.points ?? 0;
    const isDynamic = currentQuestion?.dynamicScoring ?? false;
    return (
      <div className="player-page">
        <div className="player-card">
          <div className="player-header">
            <span className="player-group" style={{ borderColor: groupColor || undefined }}>{groupName}</span>
            <span
              className={`scoring-icon-wrap ${isDynamic ? 'scoring-dynamic' : 'scoring-static'}`}
              title={isDynamic ? 'Score dynamique : répondre vite rapporte plus' : 'Score fixe'}
            >⏱</span>
          </div>
          <div className={`reveal-result ${correct === true ? 'reveal-correct' : correct === false ? 'reveal-wrong' : 'reveal-neutral'}`}>
            <div className="reveal-icon">{correct === true ? '✅' : correct === false ? '❌' : '⏱'}</div>
            <div className="reveal-text">
              {correct === true && `Bonne réponse ! +${pts} pts`}
              {correct === false && 'Mauvaise réponse'}
              {correct === null && 'Non répondu'}
            </div>
            {revealResult?.correctAnswer && (
              <div className="reveal-answer">
                Réponse : <strong>{revealResult.correctAnswer}</strong>
              </div>
            )}
            {(revealResult?.songTitle || revealResult?.artist) && (
              <div className="reveal-song">
                🎵 {revealResult.songTitle}{revealResult.songTitle && revealResult.artist ? ' — ' : ''}{revealResult.artist}
              </div>
            )}
          </div>
          {scores.length > 0 && <ScoreBoard scores={scores} deltas={scoreDeltas} compact />}
        </div>
      </div>
    );
  }

  // ── "Prêt ?" screen — after game:started but before first question ─────────
  if (gameJustStarted && gameStatus === 'in_progress' && !currentQuestion) {
    return (
      <div className="player-page">
        <div className="player-card ready-card">
          <div className="ready-icon">🎵</div>
          <h2 className="ready-title">Prêt ?</h2>
          <p className="text-muted">La partie vient de commencer !</p>
          <p className="text-muted">La première question arrive...</p>
          <div className="ready-dots">
            <span className="ready-dot" />
            <span className="ready-dot" />
            <span className="ready-dot" />
          </div>
          <p className="player-name-badge" style={{ borderColor: groupColor || undefined }}>
            {groupName}
          </p>
        </div>
      </div>
    );
  }

  // ── Waiting lobby (before game starts) ────────────────────────────────────
  if (gameStatus === 'waiting') {
    return (
      <div className="player-page">
        <div className="player-card">
          <div className="player-waiting-icon">⏳</div>
          <h2>Salle d'attente</h2>
          <p className="text-muted">Connecté en tant que <strong style={{ color: groupColor || undefined }}>{groupName}</strong></p>
          {lobbyGroups.length > 0 && (
            <div className="lobby-players">
              <p className="lobby-title">{lobbyGroups.length} équipe{lobbyGroups.length > 1 ? 's' : ''} présente{lobbyGroups.length > 1 ? 's' : ''}</p>
              <div className="lobby-list">
                {lobbyGroups.map((g) => (
                  <div key={g.name} className={`lobby-chip ${g.name === groupName ? 'lobby-me' : ''}`}>
                    <span className="lobby-dot" style={{ background: g.color }} />
                    <span>{g.name}</span>
                    {g.name === groupName && <span className="lobby-you"> (vous)</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-muted lobby-hint">En attente du lancement par l'administrateur...</p>
        </div>
      </div>
    );
  }

  // ── Between series ─────────────────────────────────────────────────────────
  if (serieEnded || (gameStatus === 'in_progress' && !currentQuestion && !intruVote)) {
    return (
      <div className="player-page">
        <div className="player-card">
          <div className="player-waiting-icon">🎵</div>
          <h2>Série terminée !</h2>
          <p className="text-muted">En attente de la prochaine série...</p>
          {scores.length > 0 && (
            <>
              <h3 className="interim-score-title">Classement provisoire</h3>
              <ScoreBoard scores={scores} compact />
            </>
          )}
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="player-page">
        <div className="player-card">
          <div className="player-waiting-icon">🎵</div>
          <h2>Prêt pour la prochaine question...</h2>
          <p className="text-muted" style={{ color: groupColor || undefined }}>{groupName}</p>
          {scores.length > 0 && <ScoreBoard scores={scores} compact />}
        </div>
      </div>
    );
  }

  // ── Active question ────────────────────────────────────────────────────────
  const q = currentQuestion;
  const isUntimed = q.durationMs === 0;
  const questionActive = (isUntimed || timeLeftMs > 0) && !waitingReveal;

  return (
    <div className="player-page">
      <div className="player-card">
        {/* Header */}
        <div className="player-header">
          <span className="player-group" style={{ borderColor: groupColor || undefined }}>{groupName}</span>
          <span className="badge">{q.maxPoints} pts</span>
          {currentSerieName && <span className="badge badge-serie">{currentSerieName}</span>}
          <span
            className={`scoring-icon-wrap ${q.dynamicScoring ? 'scoring-dynamic' : 'scoring-static'}`}
            title={q.dynamicScoring ? 'Score dynamique : répondre vite rapporte plus' : 'Score fixe'}
          >⏱</span>
        </div>

        {/* Timer (only for timed questions) */}
        {!isUntimed && questionActive && <Timer totalMs={q.durationMs} remainingMs={timeLeftMs} />}

        {/* Question */}
        <div className="question-box">
          <p className="question-text">{q.title}</p>
        </div>

        {/* Error feedback */}
        {feedback && <div className="alert alert-danger">{feedback}</div>}

        {/* ANSWERED — waiting for reveal */}
        {hasAnswered && !waitingReveal && questionActive && (
          <div className="answered-waiting">
            <div className="answered-check">✓</div>
            <p>Réponse envoyée !</p>
            <p className="text-muted">En attente des autres joueurs...</p>
          </div>
        )}

        {waitingReveal && !hasAnswered && (
          <div className="answered-waiting">
            <div className="answered-clock">⏱</div>
            <p>Temps écoulé</p>
            <p className="text-muted">En attente des résultats de l'admin...</p>
          </div>
        )}

        {waitingReveal && hasAnswered && (
          <div className="answered-waiting">
            <div className="answered-check">✓</div>
            <p>Réponse envoyée !</p>
            {autoSubmitNotice && (
              <p className="auto-submit-notice">⏱ Soumise automatiquement à la fin du temps</p>
            )}
            <p className="text-muted">En attente des résultats de l'admin...</p>
          </div>
        )}

        {/* Answer UI — only when question is active and player hasn't answered */}
        {questionActive && !hasAnswered && (
          <>
            {/* TEXT */}
            {q.answerType === 'text' && (
              <form onSubmit={handleText} className="answer-form">
                <textarea
                  className="answer-input"
                  placeholder="Votre réponse..."
                  value={textAnswer}
                  onChange={(e) => setTextAnswer(e.target.value)}
                  disabled={submitting}
                  rows={2}
                  autoFocus
                  onFocus={() => {
                    setTimeout(() => submitBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 400);
                  }}
                />
                <button
                  ref={submitBtnRef}
                  type="submit"
                  className="btn btn-primary btn-full"
                  disabled={submitting || !textAnswer.trim()}
                >
                  {submitting ? 'Envoi...' : 'Envoyer ma réponse'}
                </button>
              </form>
            )}

            {/* BUZZER */}
            {q.answerType === 'buzzer' && (
              <div className="buzzer-area">
                {isBlocked ? (
                  <div className="buzzer-blocked">
                    <div className="buzzer-blocked-icon">🚫</div>
                    <p className="buzzer-blocked-title">Mauvaise réponse !</p>
                    <p className="buzzer-blocked-reason">Vous êtes éliminé de cette question.</p>
                    <p className="text-muted">Un autre groupe peut encore buzzer et répondre.</p>
                  </div>
                ) : buzzerState.taken && !buzzerState.myTurn ? (
                  <div className="buzzer-taken-msg">
                    <div className="buzzer-taken-dot" style={{ background: buzzerState.takenByColor || '#7c3aed' }} />
                    <p><strong>{buzzerState.takenBy}</strong> a buzzé en premier !</p>
                    <p className="text-muted">En attente du résultat...</p>
                  </div>
                ) : buzzerState.myTurn ? (
                  <div className="buzzer-my-turn">
                    <div className="buzzer-my-turn-icon">🎤</div>
                    <p className="buzz-prompt">C'est à vous ! Donnez votre réponse à l'oral.</p>
                  </div>
                ) : (
                  <button
                    className={`buzz-btn${submitting ? ' buzz-btn-loading' : ''}`}
                    onClick={handleBuzz}
                    disabled={submitting}
                  >
                    🔔
                    <span>BUZZER</span>
                  </button>
                )}
              </div>
            )}

            {/* MULTIPLE CHOICE — select then validate */}
            {q.answerType === 'multiple_choice' && (
              <div className="choices-area">
                <div className="choices-grid">
                  {q.choices.map((choice, i) => (
                    <button
                      key={i}
                      className={`choice-btn ${selectedChoice === i ? 'choice-selected' : ''}`}
                      onClick={() => setSelectedChoice(i)}
                      disabled={submitting}
                    >
                      <span className="choice-letter">{String.fromCharCode(65 + i)}</span>
                      {choice}
                    </button>
                  ))}
                </div>
                {selectedChoice !== null && (
                  <button
                    className="btn btn-primary btn-full"
                    onClick={() => handleChoice(selectedChoice)}
                    disabled={submitting}
                    style={{ marginTop: 12 }}
                  >
                    {submitting ? 'Envoi...' : 'Valider ma réponse'}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Time up, didn't answer */}
        {!questionActive && !hasAnswered && !waitingReveal && !isUntimed && timeLeftMs === 0 && (
          <div className="time-up">⏱ Temps écoulé !</div>
        )}
      </div>
    </div>
  );
}
